import { AddressBookTabs, WarningCircle } from "@phosphor-icons/react";
import { DialogProps } from "../../Dialog";
import { Modal } from "@void-orbit/ui";
import { useStore } from "../../../store";
import { Form } from "../../Form";
import { useContactModalController } from "../../../controllers/modals/contactModalController";
import { Button, Size, Variant } from "../../Buttons";

import { Contact } from "../../../models/Contact";
import { AnimatePresence, motion } from "motion/react";
import { withHookFormMask } from "use-mask-input";
import { TextField } from "../../Form/Fields/TextField";
import { Select } from "../../Form/Fields/Select";
import { Country } from "../../../models/Country";
import { CountryIsoType, Flag } from "../../Flag";
import { getPhoneMask } from "../../../utils/getPhoneMask";
import { InitialContactModalValue } from "../../../controllers/modals/contactModalController";

export interface ContactModalProps extends Omit<DialogProps, "children">{
  contact: Contact | null;
  initialContact?: InitialContactModalValue | null;
};

export const ContactModal = ({
  onClose = () => {},
  open,
  contact,
  initialContact,
}: ContactModalProps ) => {
  const {
    theme,
  } = useStore();

  const {
    form: {
      register,
      watch,
      setValue,
      formState: {
        errors,
        isValid,
      }
    },
    showMessage,
    countries,
    handleSubmit,
    toggleShowMessage,
    handleDelete,
  } = useContactModalController( contact, initialContact, open, onClose );

  const contactIsActive = Boolean( contact ) && contact?.deletedAt == null;

  return (
    <Modal
      open={ open }
      onClose={ onClose }
      title={ `${ contact?.id ? "Editar" : "Novo" } Contato` }
      closeLabel="Fechar cadastro de contato"
      icon={
        <AddressBookTabs
          weight="duotone"
          color={ theme.colors.text.default }
          size={ 32 }
        />
      }
      footer={
        showMessage.delete
          ? {
              color: "error",
              message: (
                <>
                  <WarningCircle
                    size={ 16 }
                    weight="duotone"
                  />

                  Deseja realmente excluir este contato?
                </>
              ),
              actions: (
                <>
                  <Button
                    $size={ Size.SMALL }
                    $color="error"
                    $variant={ Variant.OUTLINED }
                    onClick={() => toggleShowMessage("delete")}
                  >
                    Não
                  </Button>

                  <Button
                    $size={ Size.SMALL }
                    $color="error"
                    onClick={ handleDelete }
                  >
                    Sim
                  </Button>
                </>
              ),
            }
          : null
      }
      onFooterDismiss={() => toggleShowMessage("delete")}
    >
      <Form
        onSubmit={ handleSubmit }
      >
        <div className="inputs-view">
          <TextField
            registerProps={ register("name")}
            label="Nome"
            placeholder="Nome do contato"
            error={ errors.name }
          />

          <div className="inputs-view-row">
            <Select<Country>
              keyProperty="iso"
              name="ddi"
              label="DDI"
              options={ countries }
              style={{ width: 104 }}
              selected={ watch("ddi") as Country }
              handleSelect={( option ) => setValue("ddi", option )}
              error={ errors.ddi?.message }
              RenderOption={( country ) => (
                <>
                  <Flag
                    country={ country.iso.toLocaleLowerCase() as CountryIsoType }
                    size={ 16 }
                  />

                  <span>{ country.code }</span>
                </>
              )}
            />

            <TextField
              registerProps={ withHookFormMask( register("phone"), getPhoneMask(( watch("ddi") as Country )), {
                showMaskOnFocus: false,
                showMaskOnHover: false,
                greedy: true,
              })}
              label="Número de telefone"
              placeholder="Número de telefone"
              error={ errors.phone }
            />
          </div>
        </div>

        <div className="actions-view">
          <div className="actions-view-row">
            {
              contactIsActive
                ? (
                    <>
                      <div>
                        <AnimatePresence>
                          {
                            !showMessage.delete && (
                              <motion.div
                                initial={{
                                  scale: 0,
                                }}
                                animate={{
                                  scale: 1,
                                }}
                                exit={{
                                  scale: 0,
                                }}
                                transition={{ duration: .2 }}
                              >
                                <Button
                                  $color="error"
                                  $variant={ Variant.TEXT }
                                  onClick={() => toggleShowMessage("delete")}
                                >
                                  Excluir
                                </Button>
                              </motion.div>
                            )
                          }
                        </AnimatePresence>
                      </div>
                    </>
                  )
                : <div />
            }

            <Button
              $color="text"
              $variant={ Variant.CONTAINED }
              type="submit"
              disabled={ !isValid }
            >
              Salvar
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
